import { store } from './store';
import { checkRequired } from './checkRequired';
import { finalize } from './finalize';
import DB from '../../DAL/DB';
import { Timestamp, deleteField, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { setLoading, setProject, markClean } from '../action_creators';
import { scoreProject } from './scoreProject';
import { auth } from '../../Auth';
import conditionGroupValidation from '../Field/utils';
import ErrorProject from '../../DAL/ErrorProject';

const projects = new DB.Projects();

class ValidationError {
  constructor(errors) {
    this.errors = errors;
    this.name = `Validation Error`;
  }
}

function calculateIfDataMoreThan1MB(data) {
  // Helper function to estimate size recursively
  function estimateSize(obj) {
    let size = 0;
    if (obj === null) return 1;
    if (typeof obj === 'boolean') return 1;
    if (typeof obj === 'number') {
      if (Number.isInteger(obj)) {
        return Math.floor(Math.log2(Math.abs(obj || 1)) / 8) + 1;
      }
      return 8;
    }
    if (typeof obj === 'string') return obj.length + 1;
    if (Array.isArray(obj)) {
      size += 1;
      for (let i = 0; i < obj.length; i++) {
        size += estimateSize(obj[i]);
      }
      return size;
    }
    if (typeof obj === 'object') {
      size += 1;
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          size += key.length + 1;
          size += estimateSize(obj[key]);
        }
      }
      return size;
    }
    return 0;
  }
  const sizeInBytes = estimateSize(data);
  const maxSizeInBytes = 1048576;
  return sizeInBytes > maxSizeInBytes;
}

const retrieveQuestionList = (sections) => {
  const questionList = {};

  // Check if sections is a valid object
  if (sections && typeof sections === 'object') {
    // Iterate through each section
    Object.keys(sections).forEach(sectionKey => {
      const section = sections[sectionKey];
      // Check if section has a fields object
      if (section && section.fields && typeof section.fields === 'object') {
        // Merge fields into questionList
        Object.assign(questionList, section.fields);
      }
    });
  }

  return questionList;
};

export async function saveIntake(finish = false) {
  const state = store.getState();
  const { hiddenFields, project, sections, fields } = state;

  // really ? we can have a case where project is not there when we are trying to save it ?
  if (state.status.loading) {
    throw new Error('save_in_progress');
  }

  store.dispatch(setLoading(true));

  // Create new sections immutably
  const newSections = {};

  //console.log('Current fields before saveIntake:', project.intake.sections);

  const allquestions = retrieveQuestionList(project.intake.sections)
  //console.log('All questions in intake report', Object.keys(allquestions).length, allquestions)
  

  for (let i in sections) {
    const section = sections[i];

    const fieldsInSection = (section.order || []).map((fId) => fields[fId]).filter((j) => j);
    for (let field of fieldsInSection) {
      const { conditionGroup } = field;
      let visible = true;
      if (conditionGroup) {
          const { operand, cgList } = conditionGroup;
          if (!cgList || cgList.length === 0) {
              visible = true;
              continue;
          }
          const isDisplay = conditionGroupValidation(cgList, allquestions, operand);
          visible = isDisplay;
      } else  {
          visible = true;
      }
      field.visible = visible;
    }

    //if any field having visible false then print that in console
    const invisibleFields = fieldsInSection.filter((f) => f.visible === false);
    // if (invisibleFields.length > 0) {
    //   console.log(`Invisible fields in section ${section.id}:`, invisibleFields.map((f) => f.id));
    // } 


    newSections[i] = {
      ...section,
      fields: (section.order || []).map((fId) => fields[fId]).filter((j) => j),
    };
  }

  //console.log('New sections to be saved:', newSections);

  // Create new project with updated sections
  let updatedProject = {
    ...project,
    intake: {
      ...project.intake,
      sections: newSections,
    },
    photos: fields[project.id].images || {},
  };

  try {

  if (finish) {
    console.log(`attempting to finalize this project; error checking`);
    const errors = checkRequired(updatedProject.intake, hiddenFields);
    if (Object.keys(errors).length > 0) {
      store.dispatch(setLoading(false));
      throw new ValidationError(errors);
    }

    await finalize(updatedProject);
    switch (updatedProject.status) {
      case `Review`:
        updatedProject = {
          ...updatedProject,
          status: `Completed`,
          completed_on: Timestamp.now(),
          completed_by: {
            id: auth.currentUser.uid,
            name: auth.currentUser.displayName || auth.currentUser.email,
          },
          intakeResult: await scoreProject(updatedProject),
          reviewer: {
            name: auth.sunkaizenUser.name,
            id: auth.sunkaizenUser.id,
            email: auth.sunkaizenUser.email,
            type: auth.sunkaizenUser.type,
          },
        };
        if (!updatedProject.intakeResult) {
          store.dispatch(setLoading(false));
          throw new Error('scoring_failed');
        }
        break;
      case `In Progress`:
        if (updatedProject.intake.skipReview) {
          updatedProject = {
            ...updatedProject,
            status: `Completed`,
            completed_on: Timestamp.now(),
            completed_by: {
              id: auth.currentUser.uid,
              name: auth.currentUser.displayName || auth.currentUser.email,
            },
            intakeResult: await scoreProject(updatedProject),
            reviewer: {
              name: auth.sunkaizenUser.name,
              id: auth.sunkaizenUser.id,
              email: auth.sunkaizenUser.email,
              type: auth.sunkaizenUser.type,
            },
          };
          if (!updatedProject.intakeResult) {
            store.dispatch(setLoading(false));
            throw new Error('scoring_failed');
          }
        } else {
          updatedProject = { ...updatedProject, status: `Review` };
        }
        break;
      case `Needs Corrections`:
        updatedProject = { ...updatedProject, status: `Review` };
        break;
      default:
        break;
    }
  }

  const findQuestionValue = (qid) => {
    const sections = updatedProject.intake.sections;
    let questionValue = '';
    if (sections && Object.keys(sections).length > 0) {
      Object.keys(sections).forEach((rec) => {
        const { fields } = sections[rec];
        const fieldRec = fields.find((x) => x.id === qid);
        if (fieldRec) {
          questionValue = fieldRec.value;
        }
      });
    }
    return questionValue;
  };

  const { questions } = updatedProject.rubric || { questions: {} };
  if (questions && Object.keys(questions).length > 0) {
    const newQuestions = { ...questions };
    Object.keys(newQuestions).forEach((rec) => {
      if (newQuestions[rec].OptionActions && Object.keys(newQuestions[rec].OptionActions).length > 0) {
        const questionValue = findQuestionValue(rec);
        Object.keys(newQuestions[rec].OptionActions).forEach((oA) => {
          newQuestions[rec].OptionActions[oA].forEach((optionAction) => {
            const { conditionGroup, actionIndex, strategy } = optionAction;
            let canAddAction = false;
            if (conditionGroup) {
              const { operand, cgList } = conditionGroup;
              const isDisplay = conditionGroupValidation(cgList, fields, operand);
              canAddAction = isDisplay;
            }
            if (!conditionGroup) {
              canAddAction = true;
            }
            if (canAddAction) {
              const actionString = `${actionIndex} - ${strategy}`;
              const existingSelectedActions = newQuestions[rec].selectedActions || {};
              const existingOptionActions = existingSelectedActions[oA] || [];
              if (!existingOptionActions.includes(actionString)) {
                newQuestions[rec].selectedActions = {
                  ...existingSelectedActions,
                  [oA]: [...existingOptionActions, actionString],
                };
              }
            }
          });
        });
      } else {
        newQuestions[rec] = {
          ...newQuestions[rec],
          selectedActions: {},
        };
      }
    });
    updatedProject = {
      ...updatedProject,
      rubric: {
        ...updatedProject.rubric,
        questions: newQuestions,
      },
    };
  }

  updatedProject = {
    ...updatedProject,
    updated: Timestamp.now(),
  };

  if (updatedProject.status !== 'Completed') {
    updatedProject = {
      ...updatedProject,
      completed_on: deleteField(),
      completed_by: deleteField(),
    };
  }

    const totalData = {...project, ...updatedProject, updatedBy: auth?.sunkaizenUser?.id, updatedByName: auth?.sunkaizenUser?.name }
    
    console.log('total project data', totalData)

    await projects.updatebatch(project.id, null, {...totalData})
    await setDoc(doc(projects.docRef(project.id), 'thumbnailGenerate', 'thumbnailGenerate'), { createdAt: serverTimestamp() })
    if (calculateIfDataMoreThan1MB(totalData)) {
      const errorProject = new ErrorProject()
      setDoc(errorProject.docRef(updatedProject.id), {
        msg: `Data size is more than 1MB`,
        updatedAt: updatedProject.updated,
        updatedBy: auth?.sunkaizenUser?.id,
        updatedByName: auth?.sunkaizenUser?.name,
      });
    }

    store.dispatch(markClean());
    store.dispatch(setLoading(false));
    store.dispatch(setProject(updatedProject));
  } catch (e) {
    console.log(e);
    store.dispatch(setLoading(false));
    throw e;
  }
}